/**
 * InvoiceForm Component
 * 
 * Form for creating a new invoice or editing an existing one.
 * Polish accounting software style with:
 *   - Clean invoice card with multi-column layout
 *   - Customer selection with autocomplete
 *   - Date pickers for invoice date and deadline
 *   - Dynamic line items table
 *   - Auto-calculated totals
 */
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCheck } from '@fortawesome/free-solid-svg-icons';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import { useApi, InvoiceLineUpdatePayload } from 'api';
import { Customer } from 'types';
import { LoadingSpinner, InvoiceTotals, CustomerAutocomplete } from '../common';
import LineItemsTable from './LineItemsTable';
import { useLineItems, calculateTotals, FormLineItem, useNavigationState, useCustomerFetch, generateClientId, createEmptyLineItem } from 'app/hooks';
import {
  InvoiceFormData,
  ValidationErrors,
  OriginalInvoice,
  InvoiceFormLocationState,
  formatDateForApi,
  validateInvoiceForm,
} from './types';
import { logger } from '../../utils/logger';

const InvoiceForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const api = useApi();
  
  // Navigation state for preserving list filters when going back
  const { backToListUrl, buildDetailUrl } = useNavigationState();

  // Get clone data from location state
  const locationState = location.state as InvoiceFormLocationState | null;
  const cloneFrom = locationState?.cloneFrom;
  const isCloning = Boolean(cloneFrom);

  // Determine if we're editing or creating
  const isEditing = Boolean(id);

  // Build detail URL for navigation after save
  const backToDetailUrl = id ? buildDetailUrl(parseInt(id, 10)) : '/';

  /**
   * Handle back navigation - go to previous page
   */
  const handleBack = () => {
    navigate(-1);
  };

  /**
   * Initialize form data - handles new, edit, and clone modes
   */
  const getInitialFormData = (): InvoiceFormData => {
    // Clone mode - prefill from cloned invoice
    if (cloneFrom) {
      return {
        customer: cloneFrom.customer || null,
        date: cloneFrom.date ? new Date(cloneFrom.date) : new Date(),
        deadline: cloneFrom.deadline ? new Date(cloneFrom.deadline) : null,
        lineItems: cloneFrom.invoice_lines.length > 0
          ? cloneFrom.invoice_lines.map(line => ({
              // Don't include id for cloned lines - they should be created as new
              clientId: generateClientId(),
              product: line.product || null,
              quantity: line.quantity,
            }))
          : [createEmptyLineItem()],
      };
    }
    
    // Edit mode - will be populated by fetchInvoice
    if (isEditing) {
      return {
        customer: null,
        date: new Date(),
        deadline: null,
        lineItems: [],
      };
    }
    
    // New invoice - default state
    return {
      customer: null,
      date: new Date(),
      deadline: null,
      lineItems: [createEmptyLineItem()],
    };
  };

  // Form state - start with appropriate initial data
  const [formData, setFormData] = useState<InvoiceFormData>(getInitialFormData);

  // Loading and error states
  const [isLoading, setIsLoading] = useState(isEditing);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Validation errors for inline display
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

  // Original invoice data (for edit mode)
  const [originalInvoice, setOriginalInvoice] = useState<OriginalInvoice | null>(null);

  // Request ID ref to prevent race conditions
  const latestRequestIdRef = useRef(0);

  // Customer fetching utilities (DRY - shared with InvoiceDetail)
  const { fetchCustomerById } = useCustomerFetch();

  /**
   * Fetch invoice data when editing
   * 
   * Always fetches fresh data from API to ensure consistency.
   * Uses request ID guard to prevent race conditions.
   * Previously used location state for optimization, but this caused stale data issues.
   */
  const fetchInvoice = useCallback(async () => {
    if (!id) return;

    const invoiceId = parseInt(id, 10);
    
    // Generate a new request ID for this fetch
    const requestId = ++latestRequestIdRef.current;

    // Always fetch fresh data from API to ensure consistency
    setIsLoading(true);
    setError(null);

    try {
      const { data } = await api.getInvoice({ id: invoiceId });

      // Only update state if this is still the latest request
      if (requestId !== latestRequestIdRef.current) {
        return; // Discard stale response
      }

      // Check if invoice is finalized
      if (data.finalized) {
        setError('This invoice is finalized and cannot be edited.');
        setIsLoading(false);
        return;
      }

      setOriginalInvoice({
        id: data.id,
        customer_id: data.customer_id,
        finalized: data.finalized,
        invoice_lines: data.invoice_lines,
      });

      // Get customer data efficiently via invoices list endpoint
      let customer: Customer | null = null;
      
      // First check if customer is embedded in response
      const embeddedCustomer = (data as any).customer as Customer | undefined;
      if (embeddedCustomer && embeddedCustomer.id === data.customer_id) {
        customer = embeddedCustomer;
      } else if (data.customer_id) {
        // Fetch customer via invoices list (efficient - single call)
        customer = await fetchCustomerById(data.customer_id);
        
        // Check again after async customer fetch
        if (requestId !== latestRequestIdRef.current) {
          return;
        }
      }

      // Convert invoice data to form data
      // IMPORTANT: Use line.label for display consistency with view page.
      // The line.product object might have different/updated data, but line.label
      // is what was saved with the invoice and what the view page displays.
      // Also store line.price and line.tax to match view page totals.
      setFormData({
        customer,
        date: data.date ? new Date(data.date) : null,
        deadline: data.deadline ? new Date(data.deadline) : null,
        lineItems: data.invoice_lines.map((line: any) => ({
          id: line.id,
          clientId: generateClientId(),
          // Use line.label as the product label for consistency with view page
          product: line.product ? {
            ...line.product,
            label: line.label || line.product.label,
          } : null,
          quantity: line.quantity,
          // Store the original line price/tax to match view page totals
          storedPrice: line.price,
          storedTax: line.tax,
        })),
      });
    } catch (err) {
      if (requestId !== latestRequestIdRef.current) {
        return;
      }
      setError('Failed to load invoice. Please try again.');
      logger.error('Error fetching invoice:', err);
    } finally {
      if (requestId === latestRequestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [api, id, fetchCustomerById]);

  useEffect(() => {
    if (isEditing) {
      fetchInvoice();
    }
  }, [isEditing, fetchInvoice]);

  /**
   * Handle customer change
   */
  const handleCustomerChange = (customer: Customer | null) => {
    setFormData(prev => ({ ...prev, customer }));
    if (customer) {
      clearValidationError('customer');
    }
  };

  /**
   * Handle date change
   */
  const handleDateChange = (date: Date | null) => {
    setFormData(prev => ({ ...prev, date }));
  };

  /**
   * Handle deadline change
   */
  const handleDeadlineChange = (deadline: Date | null) => {
    setFormData(prev => ({ ...prev, deadline }));
  };

  // Line items management using useLineItems hook
  const lineItemsManager = useLineItems(formData.lineItems, {
    onChange: (lineItems: FormLineItem[]) => setFormData(prev => ({ ...prev, lineItems })),
    onProductSelected: () => clearValidationError('lineItems'),
  });
  
  /**
   * Clear a specific validation error when user interacts with field
   */
  const clearValidationError = (field: keyof ValidationErrors) => {
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    const errors = validateInvoiceForm(formData);
    setValidationErrors(errors);
    
    if (Object.keys(errors).length > 0) {
      return;
    }

    setIsSaving(true);

    try {
      if (isEditing && originalInvoice) {
        // Update existing invoice
        const linesToUpdate: InvoiceLineUpdatePayload[] = formData.lineItems
          .filter(item => item.product)
          .map(item => {
            const product = item.product!;
            // Calculate line price and tax from the current product
            // price = unit_price * quantity (total including tax)
            // tax = unit_tax * quantity
            const linePrice = (parseFloat(product.unit_price) * item.quantity).toFixed(2);
            const lineTax = (parseFloat(product.unit_tax) * item.quantity).toFixed(2);
            
            // Include all fields needed to properly update the line
            // IMPORTANT: Must include label, unit, vat_rate, price, tax so they update when product changes
            const lineData: InvoiceLineUpdatePayload = {
              product_id: product.id,
              quantity: item.quantity,
              label: product.label,
              unit: product.unit,
              vat_rate: product.vat_rate,
              price: linePrice,
              tax: lineTax,
            };
            if (item.id !== undefined) {
              lineData.id = item.id;
            }
            return lineData;
          });

        // Find deleted lines (lines in original that aren't in form)
        const currentIds = formData.lineItems
          .filter(item => item.id)
          .map(item => item.id);
        
        const deletedLines: InvoiceLineUpdatePayload[] = originalInvoice.invoice_lines
          .filter(line => !currentIds.includes(line.id))
          .map(line => ({
            id: line.id,
            _destroy: true,
          }));

        await api.putInvoice(
          { id: parseInt(id!, 10) },
          {
            invoice: {
              id: originalInvoice.id,
              customer_id: formData.customer!.id,
              date: formatDateForApi(formData.date),
              deadline: formatDateForApi(formData.deadline),
              invoice_lines_attributes: [...linesToUpdate, ...deletedLines],
            },
          }
        );

        // After editing, navigate back without passing invoice data in state.
        // This forces InvoiceDetail to fetch fresh data from API, ensuring
        // the user sees the correct updated invoice_lines (with full product data).
        // The API response from PUT may not include complete product objects.
        navigate(backToDetailUrl);
      } else {
        // Create new invoice
        const { data } = await api.postInvoices(null, {
          invoice: {
            customer_id: formData.customer!.id,
            date: formatDateForApi(formData.date),
            deadline: formatDateForApi(formData.deadline),
            invoice_lines_attributes: formData.lineItems
              .filter(item => item.product)
              .map(item => ({
                product_id: item.product!.id,
                quantity: item.quantity,
              })),
          },
        });

        // Navigate to the new invoice detail with list params preserved
        // InvoiceDetail will fetch fresh data from API
        navigate(buildDetailUrl(data.id));
      }
    } catch (err) {
      logger.error('Error saving invoice:', err);
      alert('Failed to save invoice. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Handle cancel
   */
  const handleCancel = () => {
    if (isEditing) {
      navigate(backToDetailUrl);
    } else {
      navigate(backToListUrl);
    }
  };

  // Memoize totals calculation (must be before early returns to follow hooks rules)
  const totals = useMemo(() => calculateTotals(formData.lineItems), [formData.lineItems]);

  // Loading state
  if (isLoading) {
    return <LoadingSpinner message="Loading invoice..." />;
  }

  // Error state (e.g., trying to edit finalized invoice)
  if (error) {
    return (
      <div className="invoice-card">
        <div className="invoice-card-header">
          <h1 className="invoice-card-title">Error</h1>
        </div>
        <div style={{ padding: '48px 24px', textAlign: 'center' }}>
          <p style={{ fontSize: '16px', color: 'var(--color-gray-600)', marginBottom: '24px' }}>{error}</p>
          <button onClick={handleBack} className="btn btn-primary">
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Back Link */}
      <button onClick={handleBack} className="btn btn-ghost mb-3">
        <FontAwesomeIcon icon={faArrowLeft} />{' '}
        Back
      </button>

      {/* Invoice Card */}
      <form onSubmit={handleSubmit}>
        <div className="invoice-card">
          {/* Invoice Header */}
          <div className="invoice-card-header">
            <h1 className="invoice-card-title">
              {isEditing 
                ? `Invoice nr ${id}/${new Date().getFullYear()}` 
                : (isCloning ? 'Clone Invoice' : 'New Invoice')}
            </h1>
            <div className="invoice-header-badges">
              <span className="badge badge-secondary">Draft</span>
            </div>
          </div>

          {/* Invoice Details Grid - 3 columns like view page */}
          <div className="invoice-details-grid">
            {/* Left Column - Customer */}
            <div className="invoice-details-column">
              <div className="invoice-form-field">
                <span className="invoice-detail-label">Customer *</span>
                <div className={`invoice-form-input ${validationErrors.customer ? 'has-error' : ''}`}>
                  <CustomerAutocomplete
                    value={formData.customer}
                    onChange={handleCustomerChange}
                  />
                </div>
                {validationErrors.customer && (
                  <span className="validation-error">{validationErrors.customer}</span>
                )}
              </div>
              {formData.customer && (
                <div className="customer-preview">
                  <div className="customer-address">
                    {formData.customer.address}<br />
                    {formData.customer.zip_code} {formData.customer.city}<br />
                    {formData.customer.country}
                  </div>
                </div>
              )}
            </div>

            {/* Middle Column - Dates */}
            <div className="invoice-details-column">
              <div className="invoice-form-field">
                <span className="invoice-detail-label">Invoice Date</span>
                <div className="invoice-form-input">
                  <DatePicker
                    selected={formData.date}
                    onChange={handleDateChange}
                    dateFormat="dd-MM-yyyy"
                    className="form-control"
                    placeholderText="Select date"
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    todayButton="Today"
                    portalId="datepicker-portal"
                  />
                </div>
              </div>
              <div className="invoice-form-field">
                <span className="invoice-detail-label">Due Date</span>
                <div className="invoice-form-input">
                  <DatePicker
                    selected={formData.deadline}
                    onChange={handleDeadlineChange}
                    dateFormat="dd-MM-yyyy"
                    className="form-control"
                    placeholderText="Select deadline"
                    minDate={formData.date || undefined}
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    todayButton="Today"
                    portalId="datepicker-portal"
                  />
                </div>
              </div>
            </div>

            {/* Right Column - Currency */}
            <div className="invoice-details-column">
              <div className="invoice-detail-row">
                <span className="invoice-detail-label">Currency</span>
                <span className="invoice-detail-value">EUR</span>
              </div>
            </div>
          </div>

          {/* Line Items Table */}
          <LineItemsTable
            lineItems={formData.lineItems}
            validationError={validationErrors.lineItems}
            onUpdateProduct={lineItemsManager.updateLineProduct}
            onUpdateQuantity={lineItemsManager.updateLineQuantity}
            onRemoveLine={lineItemsManager.removeLine}
            onAddLine={lineItemsManager.addLine}
          />

          {/* Totals Summary */}
          {formData.lineItems.some(item => item.product) && (
            <InvoiceTotals
              subtotal={totals.subtotal}
              tax={totals.tax}
              total={totals.total}
            />
          )}
        </div>

        {/* Form Actions - inline row with view page style */}
        <div className="invoice-form-actions">
          <button
            type="button"
            className="action-panel-btn-inline"
            onClick={handleCancel}
            disabled={isSaving}
          >
            <span className="action-panel-icon-inline">
              <FontAwesomeIcon icon={faArrowLeft} />
            </span>
            <span className="action-panel-label">Cancel</span>
          </button>
          <button
            type="submit"
            className="action-panel-btn-inline action-panel-btn-inline-filled"
            disabled={isSaving}
          >
            <span className="action-panel-icon-inline">
              {isSaving ? (
                <span className="spinner-small" />
              ) : (
                <FontAwesomeIcon icon={faCheck} />
              )}
            </span>
            <span className="action-panel-label">
              {isSaving ? 'Saving...' : (isEditing ? 'Save Changes' : 'Create Invoice')}
            </span>
          </button>
        </div>
      </form>
      
      {/* Portal container for DatePicker to ensure proper z-index */}
      <div id="datepicker-portal" />
    </>
  );
};

export default InvoiceForm;
